import { CheckCircle2, Download, FileCheck2, FileSpreadsheet, Upload } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { api, post } from "../api";
import { ErrorState, InlineAlert, PageHeader } from "../components/common";
import { useAppContext } from "../context";
import { profitQueryString } from "../profit-query";

type ImportPreview = {
  importId: string;
  duplicate: boolean;
  status: string;
  preview: { totalRows: number; validRows: number; warningRows: number; errorRows: number; errors: string[]; warnings: string[]; sheets: Record<string, number> };
};

export function ImportsPage() {
  const { bootstrap, storeId, platformFilter, siteFilter, month, periodGranularity, periodAnchor, reportingCurrency, profitMode } = useAppContext();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const canWrite = bootstrap.user.role === "admin" || bootstrap.user.role === "finance_maker";

  async function submit(event: FormEvent) {
    event.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setBusy(true);
    setNotice(null);
    try {
      const data = new FormData();
      data.append("file", file);
      const result = await api<ImportPreview>("/api/imports/preview", { method: "POST", body: data });
      setPreview(result);
      setNotice(result.duplicate ? "这个文件已经预检或提交过，没有生成重复数据。" : "预检完成，请核对错误和警告后提交。 ");
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    setBusy(true);
    try {
      await post("/api/imports/commit", { importId: preview.importId });
      setNotice("导入已提交。有效数据已落库，警告行已进入数据质量队列。");
      setPreview((current) => current ? { ...current, status: "committed" } : current);
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  const exportUrl = `/api/exports/profit.xlsx?${profitQueryString({ storeId, platform: platformFilter, site: siteFilter, month, granularity: periodGranularity, periodAnchor, reportingCurrency, profitMode })}`;
  return <div className="profit-page imports-page">
    <PageHeader eyebrow="Excel闭环" title="标准模板进，审计报告出" description="首版只接受系统标准.xlsx模板。原始文件、哈希、逐行预检与提交结果都会留存，重复文件不会重复入账。" />
    {notice ? <InlineAlert tone={notice.startsWith("导入已") || notice.startsWith("预检完成") ? "success" : "warning"}>{notice}</InlineAlert> : null}
    <div className="import-actions-grid">
      <section className="import-card template"><FileSpreadsheet size={25} /><div><span className="eyebrow">步骤一</span><h2>下载标准模板</h2><p>九张工作表覆盖SKU成本、订单、退款、费用、广告、达人、结算和汇率。</p></div><a className="button secondary" href="/api/imports/template"><Download size={16} /> 下载模板</a></section>
      <form className="import-card upload" onSubmit={submit}><Upload size={25} /><div><span className="eyebrow">步骤二</span><h2>上传并预检</h2><p>硬错误阻止提交，业务缺失作为警告隔离，不会按0抬高利润。</p></div><input ref={fileRef} type="file" accept=".xlsx" required disabled={!canWrite || busy} /><button className="button primary" disabled={busy}>{busy ? "正在处理…" : "开始预检"}</button></form>
      <section className="import-card export"><FileCheck2 size={25} /><div><span className="eyebrow">随时导出</span><h2>导出当前利润报告</h2><p>沿用页面筛选条件，包含摘要、订单明细、费用构成和Checks。</p></div><a className="button secondary" href={exportUrl}><Download size={16} /> 导出Excel</a></section>
    </div>
    {preview ? <section className="ledger-section import-preview">
      <div className="section-heading"><div><span className="eyebrow">预检结果</span><h2>{preview.preview.totalRows} 行 · {preview.status}</h2></div><CheckCircle2 size={20} /></div>
      <div className="preview-totals"><div><span>可提交</span><strong>{preview.preview.validRows}</strong></div><div><span>带警告</span><strong>{preview.preview.warningRows}</strong></div><div className={preview.preview.errorRows ? "danger" : ""}><span>硬错误</span><strong>{preview.preview.errorRows}</strong></div></div>
      {preview.preview.errors.length ? <ErrorState message={preview.preview.errors.join("；")} /> : null}
      <div className="sheet-counts">{Object.entries(preview.preview.sheets).filter(([, count]) => count).map(([name, count]) => <span key={name}>{name}<b>{count}</b></span>)}</div>
      <div className="import-commit"><button className="button primary" disabled={busy || preview.status === "failed" || preview.status === "committed"} onClick={() => void commit()}>{preview.status === "committed" ? "已提交" : "确认提交"}</button></div>
    </section> : null}
  </div>;
}
